import React, { useState, useEffect } from 'react';
import { Row, Col, DropdownToggle, DropdownMenu, DropdownItem, Spinner, Dropdown, Collapse, Button } from 'reactstrap';
import { useMutation } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { unitStatusUpdate } from 'endpoints/builder/builderPropertyListingDetails';
import RejectionReasonModal from '../BuilderDetail/RejectionReasonModal';
import { formatFileSize } from 'lib/utils/formatters';

interface AdditionalDocument {
    id: string;
    file_name: string;
    file_type: string;
    file_size: number;
    file_class: string;
    mimetype: string;
    title: string;
    description: string;
    associated_entity_id: string;
    file_url: string;
    rejection_reason: string | null;
    status: string;
    createdAt: string;
    updatedAt: string;
}

interface AdditionalDocumentsProps {
    additionalDocumentsData: AdditionalDocument[];
    onStatusUpdate?: (documentId: string, newStatus: string) => void;
}

const statusOptions = [
    { value: 'accepted', label: 'Accepted' },
    { value: 'pending', label: 'Pending' },
    { value: 'rejected', label: 'Rejected' },
];

const AdditionalDocuments: React.FC<AdditionalDocumentsProps> = ({ additionalDocumentsData, onStatusUpdate }) => {
    const selectedPropertyUnitId = useSelector((state: any) => state.Builder.selectedPropertyUnitId);

    // State management following PropertyDocuments pattern
    const [data, setData] = useState<AdditionalDocument[]>([]);
    const [dropdownOpen, setDropdownOpen] = useState<{ [key: number]: boolean }>({});
    const [updatingRow, setUpdatingRow] = useState<number | null>(null);
    const [accordionOpen, setAccordionOpen] = useState<{ [key: string]: boolean }>({});

    // Rejection modal states
    const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
    // eslint-disable-next-line
    const [_rejectionReason, setRejectionReason] = useState('');
    const [pendingRejectionData, setPendingRejectionData] = useState<{
        rowIndex: number;
        docId: string;
        prevSnapshot: AdditionalDocument[];
    } | null>(null);

    // Sync data with prop changes (like useEffect in PropertyDocuments)
    useEffect(() => {
        setData(additionalDocumentsData);
    }, [additionalDocumentsData]);

    // Status update mutation (similar to PropertyDocuments)
    const updateStatusMutation = useMutation<any, any, { propertyUnitId: string, documentId: string, type: string, status: string, rejection_reason?: string }>({
        mutationFn: (payload) => unitStatusUpdate(payload),
    });

    // Handle rejection confirmation from modal
    const handleRejectionConfirm = async (finalReason: string) => {
        if (!pendingRejectionData || !finalReason.trim()) {
            toast.error('Rejection reason is required');
            return;
        }

        const { rowIndex, docId } = pendingRejectionData;

        // Close modal first
        setRejectionModalOpen(false);

        // Process the rejection with reason
        await processStatusUpdate('rejected', rowIndex, docId, finalReason);

        // Clean up after API call completes
        setPendingRejectionData(null);
        setRejectionReason('');
    };

    // Handle modal cancellation
    const handleRejectionCancel = () => {
        // Simply close modal without any status change
        setRejectionModalOpen(false);
        setPendingRejectionData(null);
        setRejectionReason('');
        // No API call, no status change - everything remains as it was
    };

    // Function to process status update (same pattern as PropertyDocuments)
    const processStatusUpdate = async (status: string, rowIndex: number, docId: string, reason?: string) => {
        // For rejection, don't do optimistic update - wait for API success
        const prevSnapshot = [...data];
        
        // Only do optimistic update for non-rejection statuses
        if (status !== 'rejected') {
            setData(prevState => prevState.map((doc, idx) => idx === rowIndex ? { ...doc, status } : doc));
        }
        
        setUpdatingRow(rowIndex);

        try {
            const document = data[rowIndex];
            const payload: any = {
                propertyUnitId: selectedPropertyUnitId,
                documentId: docId,
                type: document?.file_type,
                status
            };

            // Add rejection reason if provided
            if (reason && status === 'rejected') {
                payload.rejection_reason = reason;
            }

            const response = await updateStatusMutation.mutateAsync(payload);
            toast.success('Status updated successfully');

            // Update the document state ONLY after API success
            if (status === 'rejected') {
                // For rejection, update both status and rejection reason after success
                setData(prevState => prevState.map((doc, idx) =>
                    idx === rowIndex ? {
                        ...doc,
                        status,
                        rejection_reason: reason || response?.data?.rejection_reason || response?.data?.message || 'Document rejected'
                    } : doc
                ));
            } else {
                // For other statuses, just update the status (already done optimistically above)
                // But ensure we clear any existing rejection reason
                setData(prevState => prevState.map((doc, idx) =>
                    idx === rowIndex ? {
                        ...doc,
                        status,
                        rejection_reason: null
                    } : doc
                ));
            }

            // Call parent callback if provided
            if (onStatusUpdate) {
                onStatusUpdate(docId, status);
            }
        } catch (err: any) {
            // rollback on error
            setData(prevSnapshot);
            toast.error(err?.message || 'Failed to update status');
        } finally {
            setUpdatingRow(null);
        }
    };

    const handleDownload = (document: AdditionalDocument) => {
        if (!document.file_url) return;

        // Extract file extension from URL (similar to PropertyDocuments logic)
        const urlNoQuery = document.file_url?.split('?')[0]?.toLowerCase() || '';
        const extMatch = urlNoQuery.match(/\.([a-z0-9]+)$/);
        const ext = extMatch ? extMatch[1] : '';

        // Also check mimetype as fallback
        const isPdf = ext === 'pdf' || document.mimetype === 'application/pdf' || document.file_name.toLowerCase().endsWith('.pdf');

        if (isPdf) {
            // Open PDF in a new tab with noopener for security
            const a = window.document.createElement('a');
            a.href = document.file_url;
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            window.document.body.appendChild(a);
            a.click();
            window.document.body.removeChild(a);
            return;
        }

        // Otherwise force download (images and other types)
        const link = window.document.createElement('a');
        link.href = document.file_url;
        const filename = document.file_name || '';
        if (filename) {
            link.setAttribute('download', filename);
        } else {
            link.setAttribute('download', '');
        }
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
    };

    // Handle status change for each document
    const handleStatusChange = async (newStatus: string, rowIndex: number, document: AdditionalDocument) => {
        if (!selectedPropertyUnitId) {
            toast.error('Property unit ID not found');
            return;
        }

        const payloadStatus = newStatus.toString().toLowerCase();
        const currentStatus = document.status?.toString().toLowerCase();
        const docId = document.id;

        if (!docId) {
            toast.error('Missing document id');
            return;
        }

        // Check if the new status is same as current status - if yes, don't proceed
        if (payloadStatus === currentStatus) {
            // Close the dropdown if it's open
            setDropdownOpen(prev => ({ ...prev, [rowIndex]: false }));
            return;
        }

        // If rejected is selected, open the rejection reason modal
        if (payloadStatus === 'rejected') {
            const prevSnapshot = [...data];
            setPendingRejectionData({
                rowIndex,
                docId: String(docId),
                prevSnapshot
            });
            setRejectionModalOpen(true);
            return;
        }

        // Handle other statuses (accepted, pending) normally
        await processStatusUpdate(payloadStatus, rowIndex, String(docId));
    };

    return (
        <div>
            {data.length === 0 ? (
                <div className="text-center py-4">
                    <p className="text-muted mb-0">No additional documents available</p>
                </div>
            ) : (
                data.map((doc, idx) => (
                    <React.Fragment key={doc.id}>
                        <Row className="align-items-center py-3">
                            <Col xs="auto">
                                <div
                                    className="bg-light rounded-circle d-flex align-items-center justify-content-center"
                                    style={{ width: 48, height: 48 }}
                                >
                                    <i className="ri-file-pdf-line fs-3" style={{ color: '#eb5f5fff' }}></i>
                                </div>
                            </Col>
                            <Col md={3}>
                                <div style={{ fontSize: 15 }}>{doc.title || 'N/A'}</div>
                                <div style={{ fontSize: 14 }}>{formatFileSize(doc.file_size)}</div>
                            </Col>
                            <Col md={5}>
                                <div style={{ fontSize: 15 }}>{doc.description || 'N/A'}</div>
                            </Col>
                            <Col md={3} className="d-flex align-items-center gap-3 justify-content-end">
                                <span
                                    onClick={() => handleDownload(doc)}
                                    style={{
                                        color: doc.file_url ? '#000000' : '#cccccc',
                                        fontSize: 28,
                                        cursor: doc.file_url ? 'pointer' : 'not-allowed'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                    title="Download"
                                >
                                    <i className="ri-download-cloud-line"></i>
                                </span>

                                {/* Status Dropdown following PropertyDocuments pattern */}
                                <Dropdown
                                    isOpen={!!dropdownOpen[idx]}
                                    toggle={() => setDropdownOpen(prev => ({ ...prev, [idx]: !prev[idx] }))}
                                    size="sm"
                                >
                                    <DropdownToggle caret color="light" style={{ minWidth: 100 }}>
                                        {updatingRow === idx ? (
                                            <span className="d-inline-flex justify-content-center align-items-center">
                                                <Spinner size="sm" color="secondary" className="me-2" />
                                                <span className='mt-1'>Updating...</span>
                                            </span>
                                        ) : (
                                            statusOptions.find(opt => opt.value === doc.status)?.label ||
                                            (doc.status ? String(doc.status).charAt(0).toUpperCase() + String(doc.status).slice(1) : 'Select')
                                        )}
                                    </DropdownToggle>
                                    <DropdownMenu>
                                        {statusOptions.map(opt => (
                                            <DropdownItem
                                                key={opt.value}
                                                active={doc.status === opt.value}
                                                onClick={() => handleStatusChange(opt.value, idx, doc)}
                                            >
                                                {opt.label}
                                            </DropdownItem>
                                        ))}
                                    </DropdownMenu>
                                </Dropdown>
                            </Col>
                        </Row>

                        {/* Rejection Reason Accordion - Show only if status is rejected and reason exists */}
                        {doc.status === 'rejected' && doc.rejection_reason && (
                            <Row>
                                <Col xs={12}>
                                    <div
                                        className="mt-2 mb-3"
                                        style={{ position: 'relative', zIndex: accordionOpen[doc.id] ? 1050 : 1 }}
                                    >
                                        <Button
                                            color="light"
                                            size="sm"
                                            onClick={() => setAccordionOpen(prev => ({
                                                ...prev,
                                                [doc.id]: !prev[doc.id]
                                            }))}
                                            className="w-100 d-flex align-items-center justify-content-between p-2 border"
                                            style={{ backgroundColor: '#f8f9fa', borderColor: '#dee2e6' }}
                                        >
                                            <span className="d-flex align-items-center">
                                                <i className="ri-error-warning-line text-danger me-2"></i>
                                                <span style={{ fontSize: '14px', fontWeight: 500 }}>Rejection Reason</span>
                                            </span>
                                            <i className={`ri-arrow-${accordionOpen[doc.id] ? 'up' : 'down'}-s-line`}></i>
                                        </Button>
                                        <Collapse isOpen={!!accordionOpen[doc.id]}>
                                            <div
                                                className="p-3 bg-light border border-top-0 rounded-bottom"
                                                style={{
                                                    position: 'relative',
                                                    zIndex: 1051,
                                                    backgroundColor: '#f8f9fa',
                                                    boxShadow: accordionOpen[doc.id] ? '0 4px 8px rgba(0,0,0,0.15)' : 'none'
                                                }}
                                            >
                                                <p className="mb-0 text-muted" style={{ fontSize: '14px', lineHeight: '1.5' }}>
                                                    {doc.rejection_reason}
                                                </p>
                                            </div>
                                        </Collapse>
                                    </div>
                                </Col>
                            </Row>
                        )}

                        {idx !== data.length - 1 && <hr className="my-0" />}
                    </React.Fragment>
                ))
            )}

            {/* Rejection Reason Modal */}
            <RejectionReasonModal
                isOpen={rejectionModalOpen}
                toggle={handleRejectionCancel}
                setRejectionReason={setRejectionReason}
                onConfirm={handleRejectionConfirm}
            />
        </div>
    );
};

export default AdditionalDocuments;